import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { apiErrorMessage, useApiDocsManager, useDeleteSheet, useRequireApiDocsManager, useSheets } from '../api';
import { visibilityLabel, type SheetContent } from '../types';

const BASE = '/superadmin-api-docs/sheet-content';

export function SheetListPage() {
  useRequireApiDocsManager();
  const { canModify } = useApiDocsManager();
  const sheets = useSheets();
  const deleteSheet = useDeleteSheet();
  const [toDelete, setToDelete] = useState<SheetContent | null>(null);

  const columns = useMemo<ColumnDef<SheetContent>[]>(
    () => [
      {
        id: 'index',
        header: 'Sr No',
        size: 60,
        enableSorting: false,
        cell: ({ row }) => <span className="font-mono text-xs text-muted-foreground">{row.index + 1}</span>,
      },
      { accessorKey: 'sheetName', header: 'Sheet Name' },
      {
        accessorKey: 'visibility',
        header: 'Visibility',
        cell: ({ row }) => visibilityLabel(row.original.visibility),
      },
      { accessorKey: 'createdByName', header: 'Created By', cell: ({ row }) => row.original.createdByName ?? '—' },
      { accessorKey: 'updatedAt', header: 'Last Updated' },
      {
        id: 'actions',
        header: 'Action',
        size: 140,
        enableSorting: false,
        cell: ({ row }) => {
          const mine = canModify(row.original.createdBy);
          return (
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" asChild>
                <Link to={`${BASE}/view/${row.original.id}`} aria-label="View sheet">
                  <Eye className="h-3.5 w-3.5" />
                </Link>
              </Button>
              {mine && (
                <>
                  <Button size="sm" variant="ghost" asChild>
                    <Link to={`${BASE}/edit/${row.original.id}`} aria-label="Edit sheet">
                      <Pencil className="h-3.5 w-3.5" />
                    </Link>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setToDelete(row.original)}
                    aria-label="Delete sheet"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canModify],
  );

  const confirmDelete = () => {
    if (!toDelete) return;
    deleteSheet.mutate(toDelete.id, {
      onSuccess: (r) => {
        toast.success(r.message);
        setToDelete(null);
      },
      onError: (e) => toast.error(apiErrorMessage(e, 'Could not delete the sheet')),
    });
  };

  return (
    <FillPage>
      <PageHeader
        title="Sheet Content"
        description="API documentation sheets shown to your users."
        actions={
          <Button asChild>
            <Link to={`${BASE}/add`}>
              <Plus className="h-4 w-4" />
              Add Sheet
            </Link>
          </Button>
        }
      />

      <DataTable<SheetContent, unknown>
        tableId="api-docs:sheets:v2"
        columns={columns}
        data={sheets.data ?? []}
        isLoading={sheets.isLoading}
        searchPlaceholder="Search sheets…"
        emptyTitle="No sheets yet"
        emptyDescription="Create a sheet to share API details."
        getRowId={(row) => String(row.id)}
        pageSize={25}
        disableExport
      />

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Are you sure you want to delete?"
        description={<>Sheet <strong>{toDelete?.sheetName}</strong> will be removed for everyone.</>}
        confirmLabel="Delete"
        tone="destructive"
        loading={deleteSheet.isPending}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
}
